/**
 * Pure dockview build / visibility operations (架构方案 §5.7). These mutate the
 * live `DockviewApi` tree (add areas, apply capabilities, toggle visibility) and
 * are shared by the initial build, the runtime toggle, and the reset path in
 * `useDockviewManager`.
 */
import type { LayoutArea, LayoutSchema } from '@hibro/shared';
import type { DockviewApi } from 'dockview-react';

import { applyAreaViewClasses } from './area-classes';
import {
  AREA_BUILD_ORDER,
  AREA_SPECS,
  areaCaps,
  axisOf,
  isEdgeArea,
} from './area-specs';
import { findAreaConfig, isAreaEnabled, isAreaVisible } from './schema';

/**
 * Add a single area's panel to the live layout, driven by its structural
 * {@link AREA_SPECS} entry (`position` / `tabComponent`) plus the schema `size`.
 * Used by both the initial build and the runtime toggle so every area is created
 * the same way and can be dynamically shown / hidden (架构方案 §5.7). Callers
 * must run {@link configureAreas} afterwards to (re)apply each area's
 * capabilities.
 */
export function addAreaPanel(
  api: DockviewApi,
  area: LayoutArea,
  sizeOf: (area: LayoutArea) => number,
): void {
  if (isEdgeArea(area)) {
    addEdgeAreaGroup(api, area, sizeOf);
    return;
  }
  const { position, tabComponent } = AREA_SPECS[area];
  const axis = axisOf(area);
  api.addPanel({
    id: area,
    component: 'area',
    params: { area },
    ...(tabComponent ? { tabComponent } : {}),
    // Map the spec's `referenceArea` (an area id) to dockview's
    // `referencePanel`; activityBar omits the reference and docks against the
    // central grid's left edge.
    ...(position
      ? {
          position: position.referenceArea
            ? {
                direction: position.direction,
                referencePanel: position.referenceArea,
              }
            : { direction: position.direction },
        }
      : {}),
    ...(axis === 'width' ? { initialWidth: sizeOf(area) } : {}),
    ...(axis === 'height' ? { initialHeight: sizeOf(area) } : {}),
  });
}

/**
 * Create an `edge` area's shell group, then host its content panel inside it
 * (架构方案 §5.7; see file header for why chrome lives outside the gridview).
 *
 * Sizing is owned by the shell: a non-resizable bar pins `min == max == size`;
 * a resizable rail uses the spec's [minSize, maxSize] bounds with `size` as the
 * initial. Rebuild-safe — an edge group can outlive `api.clear()`, so a stale
 * one at this position is removed first.
 */
function addEdgeAreaGroup(
  api: DockviewApi,
  area: LayoutArea,
  sizeOf: (area: LayoutArea) => number,
): void {
  const spec = AREA_SPECS[area];
  const pos = spec.edge;
  if (!pos) {
    return;
  }
  if (api.getEdgeGroup(pos)) {
    api.removeEdgeGroup(pos);
  }
  const size = sizeOf(area);
  const { resizable } = areaCaps(area);
  const edgeApi = api.addEdgeGroup(pos, {
    id: `${area}__edge`,
    initialSize: size,
    minimumSize: resizable ? (spec.minSize ?? 0) : size,
    maximumSize: resizable ? (spec.maxSize ?? Number.MAX_SAFE_INTEGER) : size,
  });
  api.addPanel({
    id: area,
    component: 'area',
    params: { area },
    ...(spec.tabComponent ? { tabComponent: spec.tabComponent } : {}),
    // No `direction` → host the content *inside* the edge group.
    position: { referenceGroup: edgeApi.id },
  });
}

/**
 * Build the default grid from scratch by adding every enabled area via
 * {@link addAreaPanel} in {@link AREA_BUILD_ORDER}. The `editor` anchor is
 * always created (relative regions reference it); all other areas are gated by
 * their schema `enabled` flag (架构方案 §5.7).
 */
function buildDefaultLayout(
  api: DockviewApi,
  schema: LayoutSchema,
  sizeOf: (area: LayoutArea) => number,
): void {
  for (const area of AREA_BUILD_ORDER) {
    // The editor is the mandatory anchor; everything else follows the schema.
    if (area === 'editor' || isAreaEnabled(findAreaConfig(schema, area))) {
      addAreaPanel(api, area, sizeOf);
    }
  }
}

/**
 * Apply each area's interaction capabilities after the layout exists (fresh
 * build or `fromJSON` restore). These settings are not part of `toJSON`, so
 * they must be reapplied every time the layout is initialised (架构方案 §5.7):
 *   - drag:   hide the tab header (the drag handle) when not draggable
 *   - drop:   lock the group as a `no-drop-target` when not droppable
 *   - resize: when not resizable, pin the cross-axis size with equal min/max
 *     constraints + an immediate `setSize` so the splitview snaps; when
 *     resizable, apply the optional [minSize, maxSize] bounds (no `setSize`, so
 *     a user-/restore-provided size is preserved)
 *   - render: force the declared {@link AreaSpec.renderer} (tabbed anchors need
 *     `always` to survive a sibling document tab). `renderer` *is* serialized
 *     per panel, so re-applying it here is what lets an older stored tree adopt
 *     it without discarding the user's layout
 */
export function configureAreas(
  api: DockviewApi,
  schema: LayoutSchema,
  sizeOf: (area: LayoutArea) => number,
): void {
  for (const config of schema.areas) {
    const area = config.area;
    const panel = api.getPanel(area);
    if (!panel) {
      continue;
    }
    const spec = AREA_SPECS[area];
    const caps = areaCaps(area);
    const group = panel.group;

    // Header (tab strip) is the drag handle, so it hides with `draggable` by
    // default; `showHeader` decouples the two to keep the tab on a locked area.
    group.header.hidden = !(caps.draggable || spec.showHeader);
    group.locked = caps.droppable ? false : 'no-drop-target';
    if (spec.renderer) {
      panel.api.setRenderer(spec.renderer);
    }

    if (isEdgeArea(area)) {
      // Edge chrome: size / min / max are owned by the edge-group shell (set
      // at creation in addEdgeAreaGroup); no gridview constraints to apply.
      continue;
    }

    const axis = axisOf(area);
    if (!axis) {
      // editor: flexible center with no dock direction, so `axisOf` yields no
      // axis. It still lives in the horizontal center row, so honour an optional
      // `minSize`/`maxSize` as a *width* bound (never pins, so it stays flexible;
      // dockview relaxes it if the columns' minima can't all fit).
      if (spec.minSize !== undefined || spec.maxSize !== undefined) {
        group.api.setConstraints({
          minimumWidth: spec.minSize ?? 0,
          maximumWidth: spec.maxSize ?? Number.MAX_SAFE_INTEGER,
        });
      }
      continue;
    }
    const pin = (min: number, max: number): void => {
      group.api.setConstraints(
        axis === 'width'
          ? { minimumWidth: min, maximumWidth: max }
          : { minimumHeight: min, maximumHeight: max },
      );
    };
    if (!caps.resizable) {
      const size = sizeOf(area);
      pin(size, size);
      group.api.setSize(axis === 'width' ? { width: size } : { height: size });
    } else if (spec.minSize !== undefined || spec.maxSize !== undefined) {
      pin(spec.minSize ?? 0, spec.maxSize ?? Number.MAX_SAFE_INTEGER);
    }
  }
  applyTabStripVisibility(api);
  applyAreaViewClasses(api);
}

/**
 * Keep an empty tabbed area's strip down ({@link AreaSpec.hideHeaderWhenEmpty}).
 *
 * This has to be dockview's own `header.hidden` plus a forced relayout, and not
 * a CSS rule on the strip: a `renderer: 'always'` panel is positioned from a
 * *measured* box of its group's content container, so a strip that appears or
 * disappears behind dockview's back leaves those panels sized for the old box —
 * covering the very strip they were supposed to make room for.
 *
 * Re-applied on every layout change (opening the first document tab is what
 * raises the strip), hence the change guard: without it the forced relayout
 * would re-enter through `onDidLayoutChange`.
 */
export function applyTabStripVisibility(api: DockviewApi): void {
  let changed = false;
  for (const area of Object.keys(AREA_SPECS) as LayoutArea[]) {
    if (!AREA_SPECS[area].hideHeaderWhenEmpty) {
      continue;
    }
    const panel = api.getPanel(area);
    if (!panel) {
      continue;
    }
    // Anything in the group other than the structural anchor is an instance tab.
    const hidden = !panel.group.panels.some(
      (sibling) => sibling.id !== panel.id,
    );
    if (panel.group.header.hidden !== hidden) {
      panel.group.header.hidden = hidden;
      changed = true;
    }
  }
  if (changed) {
    forceRelayout(api);
  }
}

/** dockview's shell wrapper, created as soon as the tree owns any edge group. */
const SHELL_SELECTOR = '.dv-shell';

/**
 * A box safe to hand to `api.layout`. Rounds to device pixels and drops a
 * non-positive axis.
 *
 * The shell element is `width/height: 100%` of its container, so its
 * ResizeObserver only runs when *that* element changes size. `api.layout(0, …)`
 * still collapses the inner splitview, and nothing measures the shell again to
 * restore it. A 0×0 read during `clear()` is that measurement — refuse it.
 */
export function measuredLayoutBox(
  width: number,
  height: number,
): { readonly width: number; readonly height: number } | undefined {
  const roundedWidth = Math.round(width);
  const roundedHeight = Math.round(height);
  if (
    !Number.isFinite(roundedWidth) ||
    !Number.isFinite(roundedHeight) ||
    roundedWidth <= 0 ||
    roundedHeight <= 0
  ) {
    return undefined;
  }
  return { width: roundedWidth, height: roundedHeight };
}

/**
 * Re-run layout at the component's *current* size (to reposition `renderer:
 * 'always'` panels after a strip toggled).
 *
 * `api.layout(w, h)` addresses **two different boxes** depending on the tree:
 * with edge groups present dockview routes the call through the shell splitview,
 * so `w`/`h` must be the *shell* box (chrome included); with none it lays out the
 * grid directly. But `api.width` / `api.height` always report the *grid* box, so
 * `api.layout(api.width, api.height)` hands the inner box in as the outer one and
 * dockview subtracts the chrome a second time — and again on the next call.
 * Measured on a 1000×800 shell with header 40 / statusBar 28 / activityBar 60,
 * the grid walks 940×732 → 880×664 → 820×596 → 760×528, one step per call. That
 * was this bug: closing the last document tab drops the editor strip, which
 * lands here, and the whole centre column lost its chrome's worth of height.
 * Nothing takes it back either — the shell's ResizeObserver only fires when the
 * shell element itself changes, and it never did (it is 100% of the container).
 * A 0 box is skipped for the same reason: applying it collapses the grid for good.
 */
function forceRelayout(api: DockviewApi): void {
  const shell = api.groups[0]?.element.closest(SHELL_SELECTOR);
  if (!shell) {
    const box = measuredLayoutBox(api.width, api.height);
    if (box) {
      api.layout(box.width, box.height, true);
    }
    return;
  }
  const { width, height } = shell.getBoundingClientRect();
  const box = measuredLayoutBox(width, height);
  if (box) {
    api.layout(box.width, box.height, true);
  }
}

/**
 * Re-measure the shell (or the grid, when the tree has no shell yet) and apply
 * that box. Reset calls this on a later frame: dockview only writes
 * `renderer: 'always'` overlay geometry from a rAF while the panel is visible,
 * and a measurement taken in the same turn as `api.clear()` can be 0.
 */
export function relayoutFromShell(api: DockviewApi): void {
  forceRelayout(api);
}

export interface PreservedAreaSize {
  readonly axis: 'width' | 'height';
  readonly size: number;
}

export type PreservedAreaSizes = Map<LayoutArea, PreservedAreaSize>;

/**
 * Temporarily pin every configured area's current cross-axis size before a
 * structural dockview mutation. Only visible, resizable central-grid areas
 * with `preserveSizeOnMutation` participate.
 */
export function pinMutationPreservedAreaSizes(
  api: DockviewApi,
): PreservedAreaSizes {
  const preserved: PreservedAreaSizes = new Map();
  for (const area of Object.keys(AREA_SPECS) as LayoutArea[]) {
    const spec = AREA_SPECS[area];
    const axis = axisOf(area);
    const groupApi = api.getPanel(area)?.group.api;
    if (
      !spec.preserveSizeOnMutation ||
      !areaCaps(area).resizable ||
      !axis ||
      !groupApi
    ) {
      continue;
    }
    const size = axis === 'width' ? groupApi.width : groupApi.height;
    if (size <= 0) {
      continue;
    }
    preserved.set(area, { axis, size });
    groupApi.setConstraints(
      axis === 'width'
        ? { minimumWidth: size, maximumWidth: size }
        : { minimumHeight: size, maximumHeight: size },
    );
  }
  return preserved;
}

/**
 * Release mutation-time pins, then restore the captured sizes. Bounds must be
 * restored before `setSize`: the latter triggers the layout pass that
 * recalculates and re-enables each sash.
 */
export function restoreMutationPreservedAreaSizes(
  api: DockviewApi,
  preserved: ReadonlyMap<LayoutArea, PreservedAreaSize>,
): void {
  for (const [area, { axis, size }] of preserved) {
    const spec = AREA_SPECS[area];
    const groupApi = api.getPanel(area)?.group.api;
    if (!groupApi) {
      continue;
    }
    groupApi.setConstraints(
      axis === 'width'
        ? {
            minimumWidth: spec.minSize ?? 0,
            maximumWidth: spec.maxSize ?? Number.MAX_SAFE_INTEGER,
          }
        : {
            minimumHeight: spec.minSize ?? 0,
            maximumHeight: spec.maxSize ?? Number.MAX_SAFE_INTEGER,
          },
    );
    groupApi.setSize(axis === 'width' ? { width: size } : { height: size });
  }
}

/**
 * Force each area's schema `size` as its actual split size on a *fresh* build
 * (default / reset) only — never on restore (架构方案 §5.7).
 *
 * The `initialWidth` / `initialHeight` hints passed at `addPanel` time are
 * unreliable for relatively-docked panels: the gridview re-splits as later
 * siblings are added, so a *resizable* area can settle at dockview's internal
 * default (~100px) instead of its configured `size`, clamped only by its
 * [minSize, maxSize]. Where the configured size equals the area's min
 * (sidebar / auxiliaryBar) the clamp hides it, but an area whose default lands
 * inside its bounds (activityBar: 100 within [60, 162]) keeps the wrong size.
 * `configureAreas` deliberately skips `setSize` for resizable areas (to preserve
 * a restored/dragged size), so the deterministic default sizing is applied here
 * instead — and only from the fresh-build path, leaving restore untouched.
 * Restore additionally runs `snapRailAreaSize` so a leftover width snaps to
 * the nearer of 60 and 162. A stored width above 162 clamps to the label
 * stop, because `maxSize` equals that stop.
 *
 * Only areas with an explicit schema `size` and a sizing axis are forced; the
 * `editor` anchor (flexible, no axis) and `edge` chrome (size owned by the shell
 * at creation) are skipped.
 */
export function applyDefaultSizes(
  api: DockviewApi,
  schema: LayoutSchema,
  sizeOf: (area: LayoutArea) => number,
): void {
  for (const config of schema.areas) {
    const area = config.area;
    if (
      config.size === undefined ||
      !isAreaEnabled(config) ||
      isEdgeArea(area)
    ) {
      continue;
    }
    const axis = axisOf(area);
    if (!axis) {
      continue;
    }
    const size = sizeOf(area);
    api
      .getPanel(area)
      ?.group.api.setSize(
        axis === 'width' ? { width: size } : { height: size },
      );
  }
}

/**
 * Build the schema-default layout into a (possibly populated) dockview: clear
 * any existing tree, add every enabled area, apply each area's capabilities,
 * force the schema default sizes, then collapse the create-hide areas
 * (`enabled && !visible`). Shared by the initial fresh build and the runtime
 * layout reset (架构方案 §5.7).
 */
export function initDefaultLayout(
  api: DockviewApi,
  schema: LayoutSchema,
  sizeOf: (area: LayoutArea) => number,
): void {
  api.clear();
  buildDefaultLayout(api, schema, sizeOf);
  configureAreas(api, schema, sizeOf);
  // Fresh-build only: pin resizable areas to their configured size (the
  // addPanel initialWidth hint is unreliable; see applyDefaultSizes). Restore
  // never calls this, so user-dragged sizes survive reloads.
  applyDefaultSizes(api, schema, sizeOf);
  // Create-hide: enabled areas that default to hidden (`visible: false`) start
  // collapsed. Done after sizing so a pinned area's size is already applied
  // before dockview caches it for hide→show.
  for (const config of schema.areas) {
    if (isAreaEnabled(config) && !isAreaVisible(config)) {
      setAreaVisible(api, config.area, false);
    }
  }
}

/**
 * Show / hide an area, routing to the right dockview mechanism: edge chrome uses
 * the shell's `setEdgeGroupVisible`; central-grid areas use the group's
 * `setVisible` (dockview caches the invisible size for hide→show) (架构方案 §5.7).
 */
export function setAreaVisible(
  api: DockviewApi,
  area: LayoutArea,
  visible: boolean,
): void {
  const edge = AREA_SPECS[area].edge;
  if (edge) {
    api.setEdgeGroupVisible(edge, visible);
    return;
  }
  api.getPanel(area)?.group.api.setVisible(visible);
}

/** Whether an area is currently visible (edge-group or central-grid aware). */
export function isAreaVisibleNow(api: DockviewApi, area: LayoutArea): boolean {
  const edge = AREA_SPECS[area].edge;
  if (edge) {
    return api.isEdgeGroupVisible(edge);
  }
  return api.getPanel(area)?.group.api.isVisible ?? false;
}

/**
 * Whether an area's node currently exists in the live layout (edge group or
 * central-grid panel). Distinct from {@link isAreaVisibleNow}, which is false
 * for both a hidden *and* an absent node — the toggle flow needs to tell them
 * apart so a missing node is built rather than flipped.
 */
export function areaNodeExists(api: DockviewApi, area: LayoutArea): boolean {
  const edge = AREA_SPECS[area].edge;
  if (edge) {
    return api.getEdgeGroup(edge) != null;
  }
  return api.getPanel(area) != null;
}

/**
 * Tear down an area node (and, for a tabbed grid group, its instance tabs).
 * Used when occupancy/schema later disables an area that a restored layout
 * still contains.
 */
export function removeAreaPanel(api: DockviewApi, area: LayoutArea): void {
  if (isEdgeArea(area)) {
    const edge = AREA_SPECS[area].edge;
    if (edge && api.getEdgeGroup(edge)) {
      api.removeEdgeGroup(edge);
    }
    return;
  }
  const panel = api.getPanel(area);
  if (!panel) {
    return;
  }
  for (const sibling of [...panel.group.panels]) {
    if (sibling.id !== area) {
      api.removePanel(sibling);
    }
  }
  api.removePanel(panel);
}

/**
 * Make the live tree match schema `enabled`: add missing enabled areas, drop
 * present disabled ones. Idempotent. Returns whether anything changed so the
 * caller can re-apply capabilities and sync toggle state.
 */
export function reconcileEnabledAreas(
  api: DockviewApi,
  schema: LayoutSchema,
  sizeOf: (area: LayoutArea) => number,
): boolean {
  let changed = false;
  for (const area of AREA_BUILD_ORDER) {
    if (area === 'editor') {
      continue;
    }
    const enabled = isAreaEnabled(findAreaConfig(schema, area));
    const exists = areaNodeExists(api, area);
    if (enabled && !exists) {
      addAreaPanel(api, area, sizeOf);
      changed = true;
    } else if (!enabled && exists) {
      removeAreaPanel(api, area);
      changed = true;
    }
  }
  return changed;
}

/**
 * Re-pin pinned edge chrome after a `fromJSON` restore (架构方案 §5.7).
 *
 * dockview restores the edge groups itself, but its deserializer re-creates
 * them knowing only their `id` — the spec's `initialSize` / `minimumSize` /
 * `maximumSize` are *not* serialized, so a non-resizable bar loses its
 * `min == max == size` lock and the shell clamps the restored size to its
 * default minimum (header / statusBar would snap to ~100px on every reload).
 * Since the shell exposes no post-creation size API, we rebuild each pinned
 * edge group via {@link addEdgeAreaGroup} (which re-applies the spec sizing),
 * preserving the restored visibility.
 *
 * Only pinned (non-resizable) chrome needs this: a resizable edge area keeps
 * its restored size, so it is left untouched (rebuilding would discard the
 * user's drag).
 */
export function repinEdgeAreas(
  api: DockviewApi,
  sizeOf: (area: LayoutArea) => number,
): void {
  for (const area of AREA_BUILD_ORDER) {
    if (!isEdgeArea(area) || !areaNodeExists(api, area)) {
      continue;
    }
    if (areaCaps(area).resizable) {
      continue;
    }
    const wasVisible = isAreaVisibleNow(api, area);
    addEdgeAreaGroup(api, area, sizeOf);
    if (!wasVisible) {
      setAreaVisible(api, area, false);
    }
  }
}
